import { Card, Center, Stack, Text, Alert } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { forwardRef, useImperativeHandle } from "react";
import NoData from "@/components/no-data";
import Table, { THandler } from "@/components/table/Table";
import TablePagination from "@/components/table/TablePagination";
import useQueryTable from "@/components/table/useQueryTable";
import useUserStore from "@/modules/users/store";
import { columns } from "./Columns";
import { EMBAIXADORES_PESSOAS_COLLECTION_KEY, getEmbaixadoresPessoas } from "../../firestore";

const EmbaixadoresPessoasTable = forwardRef<THandler<TEmbaixadorPessoa>>((_, reference) => {
  const activeEmpresaId = useUserStore((state) => state.activeEmpresaId);

  const { table, data, isLoading, error, filters, refetch } = useQueryTable<TEmbaixadorPessoa>({
    queryKey: [...EMBAIXADORES_PESSOAS_COLLECTION_KEY],
    queryFunction: ({ sorting, filters, pagination, paginationCursors }) => {
      if (!activeEmpresaId) throw new Error("Empresa não selecionada");
      return getEmbaixadoresPessoas(activeEmpresaId, {
        sorting,
        filters,
        pagination,
        paginationCursors
      });
    },
    subjectType: "TEmbaixadorPessoa",
    columns,
    enabled: !!activeEmpresaId
  });

  useImperativeHandle(reference, () => ({
    table,
    data,
    isLoading,
    error,
    filters
  }));

  /**
   *
   */
  function handleErrorClose() {
    void refetch();
  }

  if (error) {
    return (
      <Alert
        icon={<IconAlertCircle size="1rem" />}
        title="Erro ao buscar embaixadores"
        color="red"
        withCloseButton
        onClose={handleErrorClose}
      >
        {error.message}
      </Alert>
    );
  }

  const isEmpty = !isLoading && data?.entries.length === 0;

  return (
    <>
      <Card.Section withBorder>
        <Table table={table} isLoading={isLoading} />
        {isEmpty && !isLoading && (
          <Center m="lg">
            <NoData m="md">
              <Stack gap="xs">
                <Text c="dimmed">Nenhum embaixador encontrado!</Text>
              </Stack>
            </NoData>
          </Center>
        )}
      </Card.Section>
      {reference && "current" in reference && reference.current !== null ? (
        <Card.Section withBorder>
          <TablePagination tableHandlers={reference.current} />
        </Card.Section>
      ) : null}
    </>
  );
});

EmbaixadoresPessoasTable.displayName = "EmbaixadoresPessoasTable";

export default EmbaixadoresPessoasTable;
